// WinnerDisplayPage.jsx — Globaler Sieger-Bildschirm, Japan Dark Prestige
// Zweiter Monitor — gesteuert via localStorage + BroadcastChannel
import React, { useState, useEffect, useCallback } from 'react';
import './WinnerDisplayPage.css';

const LS_KEY  = 'tda-winner-state';
const CHANNEL = 'tda-winner';
const CORNERS = ['topLeft', 'topRight', 'bottomLeft', 'bottomRight'];

export default function WinnerDisplayPage() {
  const [winnerData,   setWinnerData]   = useState(null);
  const [animKey,      setAnimKey]      = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Overflow hidden
  useEffect(() => {
    const prev = {
      html: document.documentElement.style.overflow,
      body: document.body.style.overflow,
    };
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow             = 'hidden';
    return () => {
      document.documentElement.style.overflow = prev.html;
      document.body.style.overflow             = prev.body;
    };
  }, []);

  // Auto-Vollbild
  useEffect(() => {
    const t = setTimeout(() => {
      document.documentElement.requestFullscreen?.({ navigationUI: 'hide' }).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, []);

  // Fullscreen-Status
  useEffect(() => {
    const onFs = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  // Chat-Widget verstecken
  useEffect(() => {
    const hide = () => {
      document.body.querySelectorAll('body > *:not(#root)').forEach(el => {
        if (!['SCRIPT', 'STYLE', 'LINK', 'META'].includes(el.tagName))
          el.style.setProperty('display', 'none', 'important');
      });
    };
    hide();
    const obs = new MutationObserver(hide);
    obs.observe(document.body, { childList: true });
    return () => obs.disconnect();
  }, []);

  // Sieger-Daten empfangen
  useEffect(() => {
    const apply = (raw) => {
      try {
        const d = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (d?.type === 'show') {
          setWinnerData(d);
          setAnimKey(k => k + 1);
        } else {
          setWinnerData(null);
        }
      } catch (e) {}
    };

    apply(localStorage.getItem(LS_KEY));

    const onStorage = (e) => { if (e.key === LS_KEY) apply(e.newValue); };
    window.addEventListener('storage', onStorage);

    let ch = null;
    try {
      ch = new BroadcastChannel(CHANNEL);
      ch.onmessage = (e) => apply(e.data);
    } catch (e) {}

    return () => {
      window.removeEventListener('storage', onStorage);
      if (ch) ch.close();
    };
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    else document.documentElement.requestFullscreen?.({ navigationUI: 'hide' }).catch(() => {});
  }, []);

  if (!isFullscreen) {
    return <SetupOverlay onFullscreen={toggleFullscreen} />;
  }

  return (
    <div className="wd-page" onClick={toggleFullscreen}>
      {/* Watermark */}
      <div className="wd-watermark" aria-hidden>勝</div>

      {/* Corner decorations */}
      {CORNERS.map(pos => (
        <div key={pos} className={`wd-corner ${pos}`}>
          <div className="wd-corner-h" />
          <div className="wd-corner-v" />
          <div className="wd-corner-dot" />
        </div>
      ))}

      {winnerData
        ? <WinnerCard key={animKey} data={winnerData} />
        : <StandbyScreen />
      }
    </div>
  );
}

// ── Setup Overlay ─────────────────────────────────────────────────────
function SetupOverlay({ onFullscreen }) {
  return (
    <div className="wd-setup">
      <div className="wd-setup-card">
        <div className="wd-setup-kanji">勝</div>
        <div className="wd-setup-title">Winner Display</div>
        <div className="wd-setup-sub">Globaler Siegerbildschirm — Japan Dark Prestige</div>
        <button className="wd-setup-btn" onClick={onFullscreen}>
          ⛶ Vollbild starten
        </button>
      </div>
    </div>
  );
}

// ── Standby ───────────────────────────────────────────────────────────
function StandbyScreen() {
  return (
    <div className="wd-standby">
      <div className="wd-standby-kanji">武</div>
      <div className="wd-standby-brand">TDA · SYSTEMS</div>
      <div className="wd-standby-sub">Tiger &amp; Dragon Association International</div>
    </div>
  );
}

// ── Winner Card ───────────────────────────────────────────────────────
function WinnerCard({ data }) {
  const { vorname, nachname, verein, winReason, divisionCode, sideColor } = data;

  const stripeClass = sideColor === 'ao'  ? 'ao'
                    : sideColor === 'aka' ? 'aka'
                    : null;

  const reasonLabel = winReason === 'ko'  ? 'K . O .'
                    : winReason === 'tko' ? 'T . K . O .'
                    : winReason === 'dq'  ? 'D I S Q .'
                    : 'S I E G E R';

  return (
    <div className="wd-winner">
      {/* Side color stripe */}
      {stripeClass && <div className={`wd-side-stripe ${stripeClass}`} />}

      {/* Kanji above */}
      <div className="wd-kana">勝利</div>

      {/* Win reason */}
      <div className="wd-reason">{reasonLabel}</div>

      {/* Separator */}
      <div className="wd-rule" />

      {/* Name */}
      <div className="wd-name">{vorname} {nachname}</div>

      {/* Club */}
      {verein && <div className="wd-club">{verein}</div>}

      {/* Thin rule */}
      <div className="wd-rule-thin" />

      {/* Division */}
      {divisionCode && <div className="wd-division">{divisionCode}</div>}
    </div>
  );
}
